import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Form from "src/components/base/form";
import { useForm, useStore } from "@tanstack/react-form";
import React from "react";
import { notify } from "src/components/base/toast";
import { Description, ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Divider } from "src/components/base/divider";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link SettingGeneral}
 */
export type SettingGeneralProps = {};

/**
 * Setting page to modify general server/network configuration
 */
function SettingGeneral(props: SettingGeneralProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            dns_base_domain: data.dns_base_domain,
            public_origin: data.public_origin,
            admin_origin: data.admin_origin,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const settings = {
                    dns_base_domain: value.dns_base_domain.toLowerCase(),
                    public_origin: value.public_origin.toLowerCase(),
                    admin_origin: value.admin_origin.toLowerCase(),
                };
                const result = await ApiInternal.settings.setGeneral(settings);
                if (result.result === "Err") {
                    if (result.error.conflicting_device_and_domain) {
                        return {
                            fields: {
                                dns_base_domain: t("error.conflicting-device-and-domain"),
                            },
                        };
                    } else {
                        throw new Error("Unknown error");
                    }
                }
                notify.success(tg("toast.saved-successfully"));
                await router.invalidate({ sync: true });
                form.reset();
            },
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);

    const domain_regex = /^([a-z](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z](?:[a-z0-9-]{0,61}[a-z0-9])?)$/;

    return (
        <div className={"flex flex-col justify-start gap-4"}>
            <Form className={"flex max-w-lg flex-col gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"dns_base_domain"}
                            validators={{
                                onChange: ({ value }) => {
                                    if (!domain_regex.test(value.toLowerCase())) {
                                        return t("error.invalid-domain-name");
                                    }
                                },
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.dns-base-domain")}</RequiredLabel>
                                    <Description>{t("description.dns-base-domain")}</Description>
                                    <Input
                                        required={true}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        maxLength={255}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage key={err}>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                        <form.Field
                            name={"public_origin"}
                            validators={{
                                onChange: ({ value }) => {
                                    try {
                                        const url = new URL(value);
                                        if (
                                            url.hash.length > 0 ||
                                            url.username.length > 0 ||
                                            url.password.length > 0 ||
                                            url.search.length > 0 ||
                                            (url.protocol != "http:" && url.protocol != "https:") ||
                                            value.endsWith("#")
                                        ) {
                                            return t("error.invalid-origin");
                                        }
                                    } catch (_) {
                                        return t("error.invalid-origin");
                                    }
                                },
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.public-origin")}</RequiredLabel>
                                    <Description>{t("description.public-origin")}</Description>
                                    <Input
                                        required={true}
                                        type={"url"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        maxLength={255}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage key={err}>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                        <form.Field
                            name={"admin_origin"}
                            validators={{
                                onChange: ({ value }) => {
                                    try {
                                        const url = new URL(value);
                                        if (
                                            url.hash.length > 0 ||
                                            url.username.length > 0 ||
                                            url.password.length > 0 ||
                                            url.search.length > 0 ||
                                            (url.protocol != "http:" && url.protocol != "https:") ||
                                            value.endsWith("#")
                                        ) {
                                            return t("error.invalid-origin");
                                        }
                                    } catch (_) {
                                        return t("error.invalid-origin");
                                    }
                                },
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.admin-origin")}</RequiredLabel>
                                    <Description>{t("description.admin-origin")}</Description>
                                    <Input
                                        required={true}
                                        type={"url"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                        maxLength={255}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage key={err}>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                        {t("button.reset")}
                    </Button>
                    <PrimaryButton type="submit" disabled={!isDirty}>
                        {t("button.save")}
                    </PrimaryButton>
                </div>
            </Form>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/general")({
    component: SettingGeneral,
    loader: async () => await ApiInternal.settings.getGeneral(),
});
